import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  arrayMove,
  type SortingStrategy
} from '@dnd-kit/sortable'
import { Tab } from '../../../hooks/multiWindow/useTabManager'
import { serializeTab } from '../../../hooks/multiWindow/tabSerialization'
import { DraggableTab } from './DraggableTab'
import { TooltipProvider } from '../../ui/tooltip'
import { usePlatform } from '../../../hooks/usePlatform'
import { dispatchAppEvent } from '../../../lib/events'
import type { TabBarProps } from './TabBar.types'
import './TabBar.scss'

const MIN_TAB_WIDTH_FOR_CLOSE = 100

/**
 * Custom collision detection that excludes the dragged item from drop candidates.
 * Without this, closestCenter may return the dragged tab itself as "over" when
 * the snap CSS transform shifts its DOM rect to the target position, causing
 * the collision rect (pointer-based) to overlap with the dragged tab's
 * re-measured droppable rect. This prevents reordering — especially on web
 * where DnD Kit's rect re-measurement timing differs from Electron.
 */
const filteredClosestCenter: typeof closestCenter = (args) => {
  const filtered = args.droppableContainers.filter(
    container => container.id !== args.active.id
  )
  return closestCenter({ ...args, droppableContainers: filtered })
}

/**
 * Custom sorting strategy that correctly handles different-width items.
 * The default horizontalListSortingStrategy shuffles measured rects via arrayMove,
 * which only works when all items have equal width. Since the active tab is wider
 * (different padding, font-weight), displaced items end up offset by several pixels.
 * This strategy recomputes exact post-reflow positions for pixel-perfect preview.
 */
const tabSortingStrategy = ({
  activeIndex,
  overIndex,
  index,
  rects,
}: Parameters<SortingStrategy>[0]) => {
  if (activeIndex === overIndex || activeIndex === -1 || overIndex === -1) return null
  if (index === activeIndex) return null // Active tab uses snap override

  // Only affect items in the displaced range
  const [lo, hi] = activeIndex < overIndex
    ? [activeIndex + 1, overIndex]
    : [overIndex, activeIndex - 1]
  if (index < lo || index > hi) return null

  // Measure gap from actual rects (accounts for CSS gap)
  const gap = rects.length >= 2
    ? Math.round(rects[1].left - (rects[0].left + rects[0].width))
    : 8

  // Compute post-reflow positions using actual widths
  const order = arrayMove(
    Array.from({ length: rects.length }, (_, i) => i),
    activeIndex,
    overIndex
  )
  const startX = rects[0]?.left ?? 0
  const newLefts: number[] = new Array(rects.length)
  let x = startX
  for (const origIdx of order) {
    newLefts[origIdx] = x
    x += rects[origIdx].width + gap
  }

  return {
    x: newLefts[index] - rects[index].left,
    y: 0,
    scaleX: 1,
    scaleY: 1,
  }
}

const TabBarComponent: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onTabClick,
  onTabClose,
  onTabPin,
  onCloseOthers,
  onReorder,
  sidebarLeftOpen,
  sidebarRightOpen,
  canCloseLastTab = true,
  tabBarRef,
  onTabHover,
  onPromotePreview,
  className = ''
}) => {
  const { isElectron, isMac } = usePlatform()

  // === Tear-off: detect drag outside TabBar to create new window ===
  const TEAR_OFF_THRESHOLD = 40 // px outside the TabBar triggers tear-off
  const tearingOffRef = useRef(false) // UI behavior: blocks reordering & sorting
  const tearOffActiveRef = useRef(false) // main-process tracking active: needs tearOffEnd()
  const cancelledTabIdRef = useRef<string | null>(null) // tab to activate after drag ends (cancel case)
  const [tearingOffTabId, setTearingOffTabId] = useState<string | null>(null)
  const tearingOffTabIdRef = useRef<string | null>(null) // mirror for IPC closures
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  // Ref mirror for activeTab ID — always up-to-date, avoids stale closures
  // in handleDragOver after cancel (where onTabClick hasn't re-rendered yet).
  const activeTabIdRef = useRef(activeTab?.id)
  activeTabIdRef.current = activeTab?.id

  // === Compact mode: hide close buttons when tabs are narrow ===
  const tabsContainerRef = useRef<HTMLDivElement>(null)
  const lastContainerWidthRef = useRef(0)
  // Initialize compact mode synchronously from last known width to avoid flash
  const [compactMode, setCompactMode] = useState(() => {
    if (lastContainerWidthRef.current > 0) {
      return lastContainerWidthRef.current / Math.max(tabs.length, 1) < MIN_TAB_WIDTH_FOR_CLOSE
    }
    return false
  })

  // Track if a pinned tab is being dragged (dims unpinned tabs)
  const [draggingPinnedTab, setDraggingPinnedTab] = useState(false)
  // Track if any drag is in progress (suppresses tooltips)
  const [isDragging, setIsDragging] = useState(false)

  // === Suppress tooltips on window blur to prevent stale hover tooltips on refocus ===
  const [windowBlurred, setWindowBlurred] = useState(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onBlur = () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
      setWindowBlurred(true)
    }
    const onFocus = () => {
      blurTimerRef.current = setTimeout(() => setWindowBlurred(false), 200)
    }
    window.addEventListener('blur', onBlur)
    window.addEventListener('focus', onFocus)
    return () => {
      window.removeEventListener('blur', onBlur)
      window.removeEventListener('focus', onFocus)
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current)
    }
  }, [])

  // Throttle drag-move events via rAF to prevent 60Hz+ dispatch
  const dragMoveRafRef = useRef(0)

  // === Snap-to-position: active tab snaps to valid drop positions ===
  const dragPositionsRef = useRef<number[]>([])
  const dragWidthsRef = useRef<number[]>([])
  const dragActiveIndexRef = useRef<number>(-1)
  const dragOverIndexRef = useRef<number | null>(null)
  const [snapX, setSnapX] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  // Over-index when an INACTIVE tab is being dragged (used to compute the
  // active tab's virtual displaced position for isFirstTabActive & adjacency).
  const [inactiveDragOverIdx, setInactiveDragOverIdx] = useState<number | null>(null)
  // Track which over-target was last processed for snap. DnD Kit only fires
  // onDragOver when `over` changes, but after tear-off cancel the cached `over`
  // might already match → onDragOver never fires. handleDragMove uses this ref
  // to detect unprocessed over-targets and calculate snap as fallback.
  const lastSnapOverRef = useRef<string | null>(null)

  // Sync compact mode immediately when tab count changes (no debounce)
  // to prevent close-button flash on space switch
  useEffect(() => {
    const el = tabsContainerRef.current
    if (el) {
      lastContainerWidthRef.current = el.offsetWidth
      const avgWidth = el.offsetWidth / Math.max(tabs.length, 1)
      setCompactMode(avgWidth < MIN_TAB_WIDTH_FOR_CLOSE)
    }
  }, [tabs.length])

  useEffect(() => {
    const el = tabsContainerRef.current
    if (!el) return
    let debounceTimer: ReturnType<typeof setTimeout>
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        lastContainerWidthRef.current = el.offsetWidth
        const avgWidth = el.offsetWidth / Math.max(tabs.length, 1)
        setCompactMode(avgWidth < MIN_TAB_WIDTH_FOR_CLOSE)
      }, 100)
    })
    observer.observe(el)
    return () => {
      clearTimeout(debounceTimer)
      observer.disconnect()
    }
  }, [tabs.length])

  // Sensors for drag and drop - must be called directly, not in useMemo
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  // Stable callback handlers using useCallback
  const handleTabClick = useCallback((tab: Tab) => {
    onTabClick(tab)
  }, [onTabClick])

  const handleCloseClick = useCallback((e: React.MouseEvent, tab: Tab) => {
    e?.stopPropagation?.()
    onTabClose(tab)
  }, [onTabClose])

  const handlePinClick = useCallback((e: React.MouseEvent, tab: Tab) => {
    e?.stopPropagation?.()
    onTabPin?.(tab)
  }, [onTabPin])

  const handleMiddleClick = useCallback((e: React.MouseEvent, tab: Tab) => {
    if (e.button === 1) { // Middle mouse button
      e.preventDefault()
      // Don't close last tab in primary window (use ref for stable callback)
      if (!canCloseLastTab && tabsRef.current.length <= 1) return
      onTabClose(tab)
    }
  }, [onTabClose, canCloseLastTab])

  // === Cross-window drop indicator: positioned between tabs based on cursor ===
  const [crossWindowDropIndex, setCrossWindowDropIndex] = useState<number | null>(null)
  const [dropIndicatorX, setDropIndicatorX] = useState(0)

  useEffect(() => {
    if (!window.api?.receive) return
    const unsub1 = window.api.receive('tab:drag-enter', () => {
      setCrossWindowDropIndex(tabs.length) // Default: end
    })
    const unsub2 = window.api.receive('tab:drag-leave', () => {
      setCrossWindowDropIndex(null)
    })
    const unsub3 = window.api.receive('tab:drop', () => {
      setCrossWindowDropIndex(null)
      tearingOffTabIdRef.current = null
      setTearingOffTabId(null)
    })
    const unsub4 = window.api.receive('tab:drag-over', ({ x }: { x: number; y: number }) => {
      const container = tabsContainerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      // Convert screen coords to local coords (screenX/Y gives window position)
      const localX = x - window.screenX - rect.left

      const tabEls = container.querySelectorAll('.tab-bar__tab')
      let dropIdx = tabEls.length
      let indicatorPos = rect.width

      for (let i = 0; i < tabEls.length; i++) {
        const tabEl = tabEls[i] as HTMLElement
        const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2
        if (localX < tabCenter) {
          dropIdx = i
          if (i === 0) {
            indicatorPos = tabEl.offsetLeft - 2
          } else {
            // Center between previous tab's right edge and current tab's left edge
            const prevTab = tabEls[i - 1] as HTMLElement
            const gapCenter = (prevTab.offsetLeft + prevTab.offsetWidth + tabEl.offsetLeft) / 2
            indicatorPos = Math.round(gapCenter - 1) // -1 for half of 3px indicator width
          }
          break
        }
      }

      // If dropping at end, position after last tab (center of gap after last tab)
      if (dropIdx === tabEls.length && tabEls.length > 0) {
        const lastTab = tabEls[tabEls.length - 1] as HTMLElement
        indicatorPos = lastTab.offsetLeft + lastTab.offsetWidth + 3
      }

      setCrossWindowDropIndex(dropIdx)
      setDropIndicatorX(indicatorPos)
    })
    return () => { unsub1?.(); unsub2?.(); unsub3?.(); unsub4?.() }
  }, [tabs.length])

  // Tear-off cancel/resume IPC listeners
  useEffect(() => {
    if (!window.api?.receive) return
    const unsubCancel = window.api.receive('tab:tear-off-cancel', () => {
      // Uncollapse tab and re-enable sorting. Save cancelled ID so
      // handleDragEnd can activate it cleanly after the drag finishes.
      cancelledTabIdRef.current = tearingOffTabIdRef.current
      tearingOffRef.current = false
      tearingOffTabIdRef.current = null
      setTearingOffTabId(null)
    })
    const unsubResume = window.api.receive('tab:tear-off-resume', (tabData: { id?: string }) => {
      tearingOffRef.current = true
      tearingOffTabIdRef.current = tabData?.id ?? null
      setTearingOffTabId(tabData?.id ?? null)
    })
    return () => { unsubCancel?.(); unsubResume?.() }
  }, [])

  // When tearingOffTabId changes, the tab layout shifts (collapse/restore).
  // Notify ContentUnionShape to re-measure the active tab position.
  // Also activate a cancelled tab immediately on uncollapse (not deferred to handleDragEnd),
  // so the user sees it active while still holding the mouse.
  useEffect(() => {
    const cancelledId = cancelledTabIdRef.current
    if (cancelledId && tearingOffTabId === null) {
      cancelledTabIdRef.current = null
      // Update ref immediately so handleDragOver (which may fire before
      // React re-renders) sees the correct activeTab ID for snap calculations.
      activeTabIdRef.current = cancelledId
      // Snap active tab to original position (override stale DnD Kit transform)
      setSnapX(0)
      const tab = tabsRef.current.find(t => t.id === cancelledId)
      if (tab) onTabClick(tab)
      // After React commits the DOM changes, re-measure positions for snap
      // calculations and tell ContentUnionShape to snap (not animate) the bump.
      requestAnimationFrame(() => {
        const container = tabsContainerRef.current
        if (container) {
          const tabEls = container.querySelectorAll('.tab-bar__tab')
          const positions: number[] = []
          const widths: number[] = []
          tabEls.forEach((el) => {
            positions.push((el as HTMLElement).offsetLeft)
            widths.push((el as HTMLElement).offsetWidth)
          })
          dragPositionsRef.current = positions
          dragWidthsRef.current = widths
        }
        dispatchAppEvent('tab-drag-end')
      })
    } else {
      dispatchAppEvent('tab-drag-end')
    }
  }, [tearingOffTabId]) // eslint-disable-line react-hooks/exhaustive-deps

  // When a tab starts tearing off, activate the next tab immediately.
  // Done via useEffect (not inline in tearOffTab) because navigate() from
  // react-router doesn't take effect inside a DnD Kit event handler.
  useEffect(() => {
    if (!tearingOffTabId || activeTab?.id !== tearingOffTabId || tabs.length <= 1) return
    const idx = tabs.findIndex(t => t.id === tearingOffTabId)
    const nextTab = tabs[idx + 1] ?? tabs[idx - 1]
    if (nextTab) onTabClick(nextTab)
  }, [tearingOffTabId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Tear-off: start tracking via main process (merge detection)
  const tearOffTab = useCallback(async (tabId: string) => {
    if (!isElectron || !window.windowAPI) return
    if (tearingOffRef.current || tearOffActiveRef.current) return // Already tearing off or main-process still tracking

    // Don't tear off the last tab in primary window
    if (!canCloseLastTab && tabs.length <= 1) return

    tearingOffRef.current = true
    tearOffActiveRef.current = true
    tearingOffTabIdRef.current = tabId
    setTearingOffTabId(tabId)
    setSnapX(null)
    setDragOverIndex(null)
    lastSnapOverRef.current = null // Reset so post-cancel handleDragMove detects over change

    const tab = tabs.find(t => t.id === tabId)
    if (!tab) {
      tearingOffRef.current = false
      tearingOffTabIdRef.current = null
      setTearingOffTabId(null)
      return
    }

    try {
      // Measure TabBar bounds in screen coordinates
      const container = tabsContainerRef.current
      const rect = container?.getBoundingClientRect()
      const sourceTabBarBounds = rect
        ? {
            x: Math.round(window.screenX + rect.left),
            y: Math.round(window.screenY + rect.top),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
          }
        : { x: 0, y: 0, width: 0, height: 0 }

      // Calculate cursor offset: where the first tab center will be in the preview window.
      // The preview has the same sidebar layout, so the tab bar starts at the same
      // horizontal position. The first (only) tab center is ~70px from the container left.
      // Vertically the tab sits centered in the 48px titlebar.
      const tabBarLeft = rect?.left ?? 0
      const cursorOffset = {
        x: Math.round(tabBarLeft + 70),
        y: 24,
      }

      const serialized = serializeTab(tab)
      await window.windowAPI.tearOffStart(serialized, {
        sourceTabBarBounds,
        uiState: {
          sidebarLeft: sidebarLeftOpen ?? true,
          sidebarRight: sidebarRightOpen ?? false,
        },
        cursorOffset,
      })
    } catch (error) {
      console.error('Failed to start tear-off tracking:', error)
      tearingOffRef.current = false
      tearingOffTabIdRef.current = null
      setTearingOffTabId(null)
    }
  }, [tabs, isElectron, canCloseLastTab, sidebarLeftOpen, sidebarRightOpen])

  // Memoize sorted tabs to prevent recalculation on every render
  const sortedTabs = useMemo(() => {
    return tabs.toSorted((a, b) => {
      if (a.isPinned && !b.isPinned) return -1
      if (!a.isPinned && b.isPinned) return 1
      return 0
    })
  }, [tabs])

  // Find active tab index for adjacency detection
  // When a tab is tearing off, treat it as absent for adjacency
  const activeTabIndex = useMemo(() => {
    if (tearingOffTabId && activeTab?.id === tearingOffTabId) return -1
    return sortedTabs.findIndex(t => t.id === activeTab?.id)
  }, [sortedTabs, activeTab?.id, tearingOffTabId])

  // During drag: virtual active index reflects where the active tab would
  // end up after the reorder. Works for both active-tab and inactive-tab drags.
  const virtualActiveIndex = useMemo(() => {
    // Active tab is being dragged → its virtual position = dragOverIndex
    if (dragOverIndex !== null) return dragOverIndex

    // Inactive tab is being dragged → compute where active tab is displaced to
    if (inactiveDragOverIdx !== null) {
      const dragFrom = dragActiveIndexRef.current
      const dragTo = inactiveDragOverIdx
      if (dragFrom >= 0 && dragFrom !== dragTo) {
        if (dragFrom > dragTo) {
          // Dragging left: items in [dragTo..dragFrom-1] shift right by 1
          if (activeTabIndex >= dragTo && activeTabIndex < dragFrom) return activeTabIndex + 1
        } else {
          // Dragging right: items in [dragFrom+1..dragTo] shift left by 1
          if (activeTabIndex > dragFrom && activeTabIndex <= dragTo) return activeTabIndex - 1
        }
      }
    }

    return activeTabIndex
  }, [dragOverIndex, inactiveDragOverIdx, activeTabIndex])

  const virtualIsFirstTabActive = virtualActiveIndex === 0
    && (isElectron ? (!isMac || sidebarLeftOpen) : true)

  // Adjacency indices: use virtual drop position so neighbor tabs update
  // their border-radius in real time during both active and inactive tab drags.
  const [adjLeft, adjRight] = useMemo(() => {
    // Active tab being dragged
    if (dragOverIndex !== null && dragOverIndex !== activeTabIndex) {
      if (dragOverIndex > activeTabIndex) {
        return [dragOverIndex, dragOverIndex + 1]
      }
      return [dragOverIndex - 1, dragOverIndex]
    }

    // Inactive tab being dragged: find neighbors from virtual order
    if (inactiveDragOverIdx !== null) {
      const dragFrom = dragActiveIndexRef.current
      const dragTo = inactiveDragOverIdx
      if (dragFrom >= 0 && dragFrom !== dragTo) {
        const n = sortedTabs.length
        const order = arrayMove(
          Array.from({ length: n }, (_, i) => i),
          dragFrom, dragTo
        )
        const vPos = order.indexOf(activeTabIndex)
        if (vPos >= 0) {
          return [
            vPos > 0 ? order[vPos - 1] : -1,
            vPos < n - 1 ? order[vPos + 1] : n
          ]
        }
      }
    }

    return [activeTabIndex - 1, activeTabIndex + 1]
  }, [dragOverIndex, inactiveDragOverIdx, activeTabIndex, sortedTabs.length])

  // Memoize SortableContext items to prevent array recreation every render
  const sortableItems = useMemo(() => sortedTabs.map(t => t.id), [sortedTabs])

  // Count pinned tabs for sorting strategy boundary
  const pinnedCount = useMemo(() => sortedTabs.filter(t => t.isPinned).length, [sortedTabs])

  // Wrap sorting strategy: disable displacement during tear-off
  // and prevent cross-group displacement (pinned ↔ unpinned)
  const activeSortingStrategy = useCallback<SortingStrategy>(
    (args) => {
      if (tearingOffRef.current) return null
      if (pinnedCount > 0) {
        const activeIsPinned = args.activeIndex < pinnedCount
        const overIsPinned = args.overIndex < pinnedCount
        if (activeIsPinned !== overIsPinned) return null
      }
      return tabSortingStrategy(args)
    },
    [pinnedCount]
  )

  // Measure tab positions at drag start for snap calculations
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const container = tabsContainerRef.current
    if (!container) return

    const tabEls = container.querySelectorAll('.tab-bar__tab')
    const positions: number[] = []
    const widths: number[] = []
    tabEls.forEach((el) => {
      positions.push((el as HTMLElement).offsetLeft)
      widths.push((el as HTMLElement).offsetWidth)
    })
    dragPositionsRef.current = positions
    dragWidthsRef.current = widths

    const idx = sortedTabs.findIndex(t => t.id === event.active.id)
    dragActiveIndexRef.current = idx
    setDraggingPinnedTab(idx >= 0 && !!sortedTabs[idx]?.isPinned)
    setIsDragging(true)

    // Initialize snap at 0 if active tab is being dragged
    if (activeTab?.id === event.active.id) {
      setSnapX(0)
    }
  }, [sortedTabs, activeTab?.id])

  // Shared snap calculation: compute snap offset for the active tab at a given
  // over-index. Called from both handleDragOver and handleDragMove (fallback).
  const computeAndApplySnap = useCallback((overId: string) => {
    const overIndex = sortedTabs.findIndex(t => t.id === overId)
    if (overIndex < 0) return
    // Block cross-group drag (pinned ↔ unpinned): snap back to own position
    const activeIdx = dragActiveIndexRef.current
    const activeIsPinned = activeIdx >= 0 && !!sortedTabs[activeIdx]?.isPinned
    const overIsPinned = !!sortedTabs[overIndex].isPinned
    if (activeIsPinned !== overIsPinned) {
      lastSnapOverRef.current = overId
      setSnapX(0)
      setDragOverIndex(dragActiveIndexRef.current)
      dragOverIndexRef.current = dragActiveIndexRef.current
      return
    }

    const positions = dragPositionsRef.current
    const widths = dragWidthsRef.current
    const activeIndex = dragActiveIndexRef.current
    if (activeIndex < 0 || activeIndex >= positions.length || overIndex >= positions.length) return

    let snap: number
    if (overIndex === activeIndex) {
      snap = 0
    } else if (overIndex > activeIndex) {
      snap = positions[overIndex] + widths[overIndex] - widths[activeIndex] - positions[activeIndex]
    } else {
      snap = positions[overIndex] - positions[activeIndex]
    }

    lastSnapOverRef.current = overId
    setSnapX(snap)
    setDragOverIndex(overIndex)
    dragOverIndexRef.current = overIndex

    requestAnimationFrame(() => {
      dispatchAppEvent('tab-drag-move')
    })
  }, [sortedTabs])

  // Snap active tab to the position of the hovered drop target.
  // Also tracks inactive-tab drag over-index for virtual active tab displacement.
  const handleDragOver = useCallback((event: DragOverEvent) => {
    if (tearingOffRef.current) return

    if (!event.over) {
      // Dragged away from all targets — reset inactive drag tracking
      setInactiveDragOverIdx(null)
      return
    }

    if (event.active.id === activeTabIdRef.current) {
      // Active tab is being dragged → snap to position
      computeAndApplySnap(event.over.id as string)
    } else {
      // Inactive tab is being dragged → track where it's hovering so we can
      // compute the active tab's virtual displaced position (for isFirstTab & adjacency)
      const overIdx = sortedTabs.findIndex(t => t.id === (event.over!.id as string))
      // Don't track cross-group drags (pinned ↔ unpinned)
      const dragIdx = dragActiveIndexRef.current
      if (overIdx >= 0 && dragIdx >= 0) {
        const dragIsPinned = !!sortedTabs[dragIdx]?.isPinned
        const overIsPinned = !!sortedTabs[overIdx]?.isPinned
        if (dragIsPinned !== overIsPinned) return
      }
      setInactiveDragOverIdx(overIdx >= 0 ? overIdx : null)
    }
  }, [computeAndApplySnap, sortedTabs])

  // Detect pointer leaving TabBar vertically during drag.
  // Also serves as fallback for snap: DnD Kit only fires onDragOver when the
  // `over` target *changes*. After tear-off cancel, DnD Kit's cached `over`
  // may already match the current target → onDragOver never fires. We detect
  // this via lastSnapOverRef and calculate snap here instead.
  const handleDragMove = useCallback((event: DragMoveEvent) => {
    // Throttle drag-move events to one per animation frame
    if (!dragMoveRafRef.current) {
      dragMoveRafRef.current = requestAnimationFrame(() => {
        dragMoveRafRef.current = 0
        dispatchAppEvent('tab-drag-move')
      })
    }

    if (!tearingOffRef.current) {
      // Fallback snap: if the current over-target wasn't processed by handleDragOver
      if (event.over && event.active.id === activeTabIdRef.current &&
          event.over.id !== lastSnapOverRef.current) {
        computeAndApplySnap(event.over.id as string)
      }
    }

    if (tearingOffRef.current) return // Already processing
    if (!isElectron) return

    const { activatorEvent, delta } = event
    if (!activatorEvent || !(activatorEvent instanceof PointerEvent)) return

    const pointerY = activatorEvent.clientY + delta.y
    const tabBarRect = tabsContainerRef.current?.getBoundingClientRect()
    if (!tabBarRect) return

    if (pointerY < tabBarRect.top - TEAR_OFF_THRESHOLD ||
        pointerY > tabBarRect.bottom + TEAR_OFF_THRESHOLD) {
      tearOffTab(event.active.id as string)
    }
  }, [isElectron, tearOffTab, computeAndApplySnap])

  // Optimized handleDragEnd with index map for O(1) lookup
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    // Capture snap state before reset (refs are cleared below)
    const capturedDragOverIndex = dragOverIndexRef.current
    const capturedActiveIndex = dragActiveIndexRef.current

    // Cancel pending throttled drag-move event
    if (dragMoveRafRef.current) {
      cancelAnimationFrame(dragMoveRafRef.current)
      dragMoveRafRef.current = 0
    }

    // Reset snap state
    setSnapX(null)
    setDragOverIndex(null)
    setInactiveDragOverIdx(null)
    setDraggingPinnedTab(false)
    setIsDragging(false)
    dragActiveIndexRef.current = -1
    dragOverIndexRef.current = null
    dragPositionsRef.current = []
    dragWidthsRef.current = []
    lastSnapOverRef.current = null

    // Final bump update after drag ends
    dispatchAppEvent('tab-drag-end')
    // Re-measure after DnD Kit transition settles
    setTimeout(() => dispatchAppEvent('tab-drag-end'), 250)

    // If a tear-off was started during this drag, tell main process to stop tracking.
    // tearOffActiveRef tracks whether startTracking was called (needs tearOffEnd).
    // tearingOffRef tracks current UI state (may be false after cancel).
    if (tearOffActiveRef.current) {
      tearOffActiveRef.current = false
      tearingOffRef.current = false
      if (isElectron && window.windowAPI) {
        window.windowAPI.tearOffEnd().catch((err: unknown) => {
            console.error('Failed to finalize tear-off:', err)
          })
      }
      // If tab is still torn off (merge or new window), skip reorder — tab:remove handles it.
      // If cancelled (tab back in bar), fall through to process any reorder at the new position.
      if (tearingOffTabIdRef.current !== null) return
    }

    const { active, over } = event

    // Active tab drag: use snap-based dragOverIndex instead of event.over.
    // The snap mechanism reliably tracks the target position, whereas closestCenter
    // may return the active tab itself as "over" (self-match) when the snap CSS
    // transform shifts the tab's DOM rect to the target position.
    const isActiveTabDrag = active.id === activeTabIdRef.current

    if (isActiveTabDrag) {
      if (over && capturedDragOverIndex !== null && capturedDragOverIndex !== capturedActiveIndex && onReorder) {
        const overTab = sortedTabs[capturedDragOverIndex]
        if (overTab) {
          const indexMap = new Map(tabs.map((tab, idx) => [tab.id, idx]))
          const oldIndex = indexMap.get(active.id as string)
          const newIndex = indexMap.get(overTab.id)
          if (oldIndex !== undefined && newIndex !== undefined) {
            if (!!tabs[oldIndex].isPinned === !!tabs[newIndex].isPinned) {
              onReorder(oldIndex, newIndex)
            }
          }
        }
      }
    } else {
      // Inactive tab drag: filteredClosestCenter already excludes self-match
      if (over && onReorder) {
        const indexMap = new Map(tabs.map((tab, idx) => [tab.id, idx]))
        const oldIndex = indexMap.get(active.id as string)
        const newIndex = indexMap.get(over.id as string)
        if (oldIndex !== undefined && newIndex !== undefined) {
          if (!!tabs[oldIndex].isPinned === !!tabs[newIndex].isPinned) {
            onReorder(oldIndex, newIndex)
          }
        }
      }
    }

  }, [tabs, sortedTabs, onReorder, isElectron])

  return (
    <div ref={tabBarRef} className={`tab-bar ${className}`}>
      {/* Tab container (no scrolling) */}
      <div className="tab-bar__container">
        <DndContext
          sensors={sensors}
          collisionDetection={filteredClosestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableItems}
            strategy={activeSortingStrategy}
          >
            <TooltipProvider>
              <div className="tab-bar__tabs" ref={tabsContainerRef}>
                {sortedTabs.map((tab, index) => (
                  <DraggableTab
                    key={tab.id}
                    tab={tab}
                    isActive={activeTab?.id === tab.id}
                    isTearingOff={tearingOffTabId === tab.id}
                    isFirstTab={activeTab?.id === tab.id && virtualIsFirstTabActive}
                    showClose={(activeTab?.id === tab.id || !compactMode) && (canCloseLastTab || tabs.length > 1)}
                    adjacency={
                      activeTabIndex >= 0 && index === adjLeft
                        ? 'left-of-active'
                        : activeTabIndex >= 0 && index === adjRight
                          ? 'right-of-active'
                          : undefined
                    }
                    snapX={activeTab?.id === tab.id ? snapX : undefined}
                    onClick={handleTabClick}
                    onClose={handleCloseClick}
                    onPin={handlePinClick}
                    onMiddleClick={handleMiddleClick}
                    onCloseOthers={onCloseOthers}
                    onPromote={onPromotePreview}
                    canClose={canCloseLastTab || tabs.length > 1}
                    onHoverChange={onTabHover}
                    dimmed={draggingPinnedTab && !tab.isPinned}
                    forceHideTooltip={windowBlurred || isDragging}
                  />
                ))}
                {/* Cross-window drop indicator: positioned between tabs */}
                {crossWindowDropIndex !== null && (
                  <div
                    className="tab-bar__drop-indicator"
                    style={{ left: dropIndicatorX }}
                  />
                )}
              </div>
            </TooltipProvider>
          </SortableContext>
        </DndContext>
      </div>


    </div>
  )
}

/**
 * Browser-style tab bar with drag-and-drop reordering, tear-off, and cross-window merge.
 *
 * @description
 * Renders a horizontal tab bar with the following capabilities:
 * - **Sortable tabs** via dnd-kit with a custom sorting strategy that handles
 *   variable-width tabs (active tab is wider than inactive tabs)
 * - **Snap-to-position** — the active tab snaps to valid drop slots during drag
 *   instead of following the cursor freely
 * - **Tear-off** — dragging a tab vertically outside the bar detaches it into
 *   a new Electron window (with preview tracking via IPC)
 * - **Cross-window merge** — tabs dragged from another window show a drop indicator
 *   and are inserted at the cursor position
 * - **Compact mode** — hides close buttons when tabs are narrower than 100px
 * - **Context menu** — right-click for close, duplicate, pin, close others, etc.
 * - **Quick Add button** — opens a new task creation window (Electron) or callback (Web)
 *
 * Keyboard: Middle-click closes a tab. Pinned tabs are sorted first and
 * cannot be reordered past unpinned tabs.
 *
 * @example
 * <TabBar
 *   tabs={visibleTabs}
 *   activeTab={activeTab}
 *   onTabClick={setActiveTab}
 *   onTabClose={closeTab}
 *   onReorder={reorderTabs}
 * />
 */
export const TabBar = React.memo(TabBarComponent)
